import styled, { css } from "styled-components";
import { colors, darkModeColors } from "../../consts";
import { FlexContainers, WrapperContainer, Paragraph2, SmallButtonStyled } from "../../components";

const MinTableHeight = css`
  min-height: ${(props) =>
    props?.tableSize
      ? props?.totalRows && props.tableSize > props.totalRows
        ? `calc(${!props.modalTable ? `44px +` : ""} 64px *${
            props.maxVisibleRows || props.totalRows
          } ${!props.modalTable ? `+ ${props.componentHeight}px` : ""})`
        : `calc(${!props.modalTable ? `44px +` : ""} 64px *${
            props.maxVisibleRows || props.tableSize
          } ${!props.modalTable ? `+ ${props.componentHeight}px` : ""})}`
      : "684px"}; // 10 rows, 1 row - 64px;

  ${(props) =>
    props?.maxTableHeight &&
    !props?.totalRows &&
    `min-height: calc(${props.maxTableHeight} + ${props.componentHeight}px) !important`};
`;

const ModalTable = css`
  ::-webkit-scrollbar-track-piece {
    margin-block: 0.5em;
  }

  table {
    tbody {
      display: block;
      width: 100%;
      table-layout: fixed;
      overflow-y: auto;
      ${(props) => !props.maxVisibleRows && `min-height: fit-content`};
      max-height: ${(props) => (props?.modalTableSmall ? "40vh" : "50vh")};
      ${MinTableHeight}
    }
  }

  table thead,
  table tbody tr {
    display: table;
    width: 100%;
  }
`;

export const TableWrapper = styled.div`
  width: 100%;
  border-radius: 16px;

  overflow-y: visible;

  overflow-x: ${(props) => (props.disableScroll ? "visible" : "hidden")};

  position: relative;
  z-index: 0;

  ${(props) => props?.showTableBorder && `border: 1px solid ${colors.RAISIN_40}`};

  ${(props) => !props?.modalTable && MinTableHeight}

  background: ${darkModeColors.WHITE_RAISIN_100};

  ${(props) => props?.modalTable && ModalTable}

  ::-webkit-scrollbar {
    height: 0px;
  }
`;

export const TableStyled = styled.table`
  width: 100%;
  overflow-x: ${(props) => (props.disableScroll ? "visible" : "scroll")};
  background: ${darkModeColors.WHITE_RAISIN_100};
  border-collapse: collapse;
  border-radius: 16px;

  ${(props) => props?.addOutline && `outline: 1px solid ${colors.RAISIN_20}`};

  ${(props) =>
    props?.withAdditionalElement &&
    `
      border-bottom-left-radius: 0;
      border-bottom-right-radius: 0;
    `}

  thead {
    tr {
      height: 44px;
      border-top: unset;
    }

    th {
      border-bottom: 1px solid ${colors.RAISIN_10};
    }
  }

  tbody {
    tr {
      position: relative;
      cursor: ${(props) => (props.hasClickableRows ? "pointer" : "default")};
      transition: 0.2s box-shadow;

      &:hover {
        box-shadow: ${(props) => (props.hasClickableRows ? "0px 4px 4px 0px #00000029" : "unset")};
      }

      &:first-child {
        border-top: unset;
      }
    }
  }

  tr {
    height: 64px;

    border-top: 1px solid ${colors.RAISIN_10};

    &:last-child {
      ${(props) =>
        !props?.componentHeight &&
        !props?.component &&
        !props?.withAdditionalElement &&
        `
        border-bottom-left-radius: 16px;
        border-bottom-right-radius: 16px;
      `}
    }

    th,
    td {
      padding-left: 24px;
    }

    th {
      > div {
        width: max-content;

        display: flex;
        flex-direction: row;
        align-items: center;
        gap: 8px;
      }
    }

    th:last-child,
    td:last-child {
      padding-right: 16px;
    }
  }
`;

export const TableContentWrapper = styled(WrapperContainer)`
  background: ${darkModeColors.WHITE_RAISIN_100};
  border-top: 1px solid ${colors.RAISIN_10};
  width: 100% !important;

  position: ${(props) => (props.modalTable ? "unset" : "absolute")};
  z-index: -1;
  text-align: center;

  ${(props) =>
    props.disableScroll &&
    `
    border-bottom-left-radius: 50%;
    border-bottom-right-radius: 50%;
  `}

  ${Paragraph2} {
    font-weight: 500;
    color: ${colors.RAISIN_50};
  }
`;

export const TableInfoWrapper = styled(TableContentWrapper)`
  padding-top: 44px;
  justify-content: flex-start;
`;

const SmallCellTextStyles = css`
  font-size: 14px;
  line-height: 26px;
`;

export const CellWrapper = styled(FlexContainers.FlexRowStart)`
  width: ${(props) => (props.width ? props.width : "100%")};

  ${(props) => props.minWidth && `min-width: ${props.minWidth}`};
  ${(props) => props.maxWidth && `max-width: ${props.maxWidth}`};
  ${(props) => props?.justifyContent && `justify-content: ${props.justifyContent}`}
  ${(props) => props?.ml && `margin-left: ${props.ml}`}
  ${(props) => props?.isSmallText && SmallCellTextStyles}
`;

export const EmptyCell = styled(Paragraph2)`
  ${(props) => props?.danger && `color: ${colors.ERROR}`};
`;

export const CellButton = styled(SmallButtonStyled)`
  height: 40px;
  width: 100%;

  padding: 0;

  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  border-color: ${darkModeColors.RAISIN_100_ISABELLINE};
`;

const RecentlyCreatedRow = css`
  background-color: ${colors.TURQUOISE_20};
  border: none !important;
  outline: 1px solid ${colors.TURQUOISE_100};
  outline-offset: -1px;

  td:first-child {
    position: relative;

    &:before {
      position: absolute;
      content: "";

      top: calc(50% - 4px);
      left: 7px;

      width: 8px;
      height: 8px;
      border-radius: 50%;

      background-color: ${colors.TURQUOISE_100};
    }
  }
`;

const SelectedRow = css`
  background-color: ${colors.TURQUOISE_20};
  border: none !important;
  outline: 1px solid ${colors.TURQUOISE_100};
  outline-offset: -1px;
`;

export const TableRowWrapper = styled.tr`
  ${(props) => props?.isRecentlyCreated && RecentlyCreatedRow}
  ${(props) => props?.isSelected && SelectedRow}
  ${(props) => props?.disabled && `pointer-events: none;`}
`;
